import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, Check, Download, ChevronDown } from 'lucide-react';
import axios from 'axios';

// Keys represent the ORIGINAL relationship-to-addict semantics (self/spouse/child/parent = who the addict is to the respondent);
// labels are phrased in first-person "who am I" wording per product request, so child/parent labels appear swapped from the key name.
const RELATIONSHIPS = [
  ['self', "Myself (I'm the addict)"],
  ['spouse', "I'm married to an addict"],
  ['child', "I'm the parent of an addict"],
  ['parent', "I'm the child of an addict"],
  ['other', "Someone else / I'd rather not say"],
];

const C = { navy: '#1B2A41', sage: '#8A9A86', rust: '#C25E4B', cream: '#F2EFE9', line: '#d9d4c7', muted: '#6b6f66' };

const Field = ({ children }) => <div style={{ flex: '1 1 200px', minWidth: 0 }}>{children}</div>;
const inputStyle = { width: '100%', padding: '0.85rem 1rem', borderRadius: '10px', border: `1px solid ${C.line}`, fontSize: '1rem', background: '#fff', color: C.navy, boxSizing: 'border-box' };

const COPY = {
  family: {
    title: 'Seven Prayers for Families Facing Addiction',
    intro1: "When someone you love is struggling, you need more than advice. These seven short prayers were written for particular moments — the nights you can't sleep, the days you blame yourself, the times nothing has changed and you've run out of words.",
    intro2: 'Each one comes with a scripture and space to write. One is for a child. One is for the person in recovery. Use the one that fits.',
    bullets: ['Instant download, completely free', 'Written for spouses, parents, children, and those in recovery', 'Every prayer paired with a promise from Scripture'],
    compact: <><strong>Seven prayers, free.</strong> For the nights you can't sleep. Sent straight to your inbox.</>,
    button: 'Send my prayers →',
    doneTitle: 'Your prayers are on their way.',
    open: 'Open the seven prayers',
  },
  addict: {
    title: 'Seven Prayers for the Night You Want to Use',
    intro1: "Written for you — not for the people worried about you. For the hour the craving is loudest, the morning after, the shame that turns up on time, and the day you're sure God is done with you.",
    intro2: "Nobody checks whether you've stayed clean before you read them. They're prayers for the middle of it.",
    bullets: ['Instant download, completely free', 'For cravings, the morning after, shame, loneliness — and the day you stayed clean', 'Every prayer paired with a promise from Scripture'],
    compact: <><strong>Seven prayers for tonight, free.</strong> Written for the one who's using — not the family. Straight to your inbox.</>,
    button: 'Send them to me →',
    doneTitle: 'They\'re on their way.',
    open: 'Open the seven prayers',
  },
};

export const PrayersOptIn = ({ variant = 'hero', source = 'homepage', onDone, dark = false, list = 'family' }) => {
  const copy = COPY[list] || COPY.family;
  const isAddict = list === 'addict';
  const [form, setForm] = useState({ first_name: '', email: '', relationship: isAddict ? 'self' : '' });
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const compact = variant === 'footer' || variant === 'slim';
  const slim = variant === 'slim';
  const [relSaved, setRelSaved] = useState(false);

  const saveRelationship = async (k) => {
    setForm((f) => ({ ...f, relationship: k }));
    try {
      await axios.post('/api/funnel/subscribe', { ...form, relationship: k, source, list });
      setRelSaved(true);
    } catch { /* silent: they already have the prayers */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setStatus('loading'); setError('');
    try {
      const { data } = await axios.post('/api/funnel/subscribe', { ...form, relationship: form.relationship || 'other', source, list });
      setResult(data); setStatus('done'); onDone?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again.'); setStatus('idle');
    }
  };

  const text = dark ? '#fff' : C.navy;
  const sub = dark ? 'rgba(255,255,255,0.82)' : '#3d4a5c';

  if (status === 'done') {
    return (
      <motion.div data-testid="prayers-optin-success" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: compact ? 'left' : 'center', color: text }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '50%', background: C.sage, color: '#fff', marginBottom: '0.75rem' }}><Check size={24} /></div>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem', margin: '0 0 0.5rem', color: text }}>{result?.message || copy.doneTitle}</h3>
        <p style={{ color: sub, margin: '0 0 1rem', fontSize: '0.95rem' }}>Check your inbox for an email from "Christian Family Recovery". You can also open them right now:</p>
        {result?.download_url && (
          <a data-testid="prayers-download-link" href={result.download_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: C.rust, color: '#fff', padding: '0.8rem 1.4rem', borderRadius: '999px', fontWeight: 600, textDecoration: 'none' }}>
            <Download size={18} /> {copy.open}
          </a>
        )}
        {!isAddict && !form.relationship && !relSaved && (
          <div data-testid="prayers-relationship-followup" style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.15)' : C.line}` }}>
            <p style={{ fontSize: '0.9rem', color: sub, margin: '0 0 0.6rem' }}>One optional question, so we only send what fits — who are you?</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: compact ? 'center' : 'flex-start' }}>
              {RELATIONSHIPS.map(([k, v]) => (
                <button key={k} type="button" data-testid={`optin-followup-${k}`} onClick={() => saveRelationship(k)} style={{ background: 'transparent', border: `1px solid ${dark ? 'rgba(255,255,255,0.35)' : C.line}`, color: text, borderRadius: '999px', padding: '0.4rem 0.9rem', fontSize: '0.85rem', cursor: 'pointer', width: 'auto' }}>{v}</button>
              ))}
            </div>
          </div>
        )}
        {relSaved && <p data-testid="prayers-relationship-saved" style={{ marginTop: '1rem', fontSize: '0.85rem', color: sub }}>Thank you — noted.</p>}
      </motion.div>
    );
  }

  return (
    <form data-testid={`prayers-optin-form-${variant}`} data-list={list} onSubmit={submit} style={{ color: text, textAlign: 'left' }}>
      {!compact && (
        <>
          <p style={{ fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', color: dark ? '#c9d2c4' : C.sage, fontWeight: 700, margin: '0 0 0.5rem', textAlign: 'left' }}>Free · Instant download</p>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: variant === 'hero' ? 'clamp(1.75rem, 3.5vw, 2.5rem)' : 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.15, margin: '0 0 1rem', color: text, textAlign: 'left' }}>
            {copy.title}
          </h2>
          <p style={{ color: sub, lineHeight: 1.7, margin: '0 0 0.75rem', fontSize: '1.0625rem', textAlign: 'left' }}>
            {copy.intro1}
          </p>
          <p style={{ color: sub, lineHeight: 1.7, margin: '0 0 1.25rem', fontSize: '1.0625rem', textAlign: 'left' }}>
            {copy.intro2}
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'grid', gap: '0.4rem', fontSize: '0.95rem', color: sub, textAlign: 'left' }}>
            {copy.bullets.map((t) => (
              <li key={t} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}><Check size={18} style={{ color: C.sage, flexShrink: 0, marginTop: 2 }} />{t}</li>
            ))}
          </ul>
        </>
      )}
      {compact && !slim && (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: sub, textAlign: 'center' }}>{copy.compact}</p>
      )}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <Field><input data-testid="optin-first-name" aria-label="First name" type="text" className="funnel-input" style={inputStyle} placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} autoComplete="given-name" /></Field>
        <Field><input data-testid="optin-email" aria-label="Email address" className="funnel-input" style={inputStyle} type="email" required placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" /></Field>
      </div>
      {slim || isAddict ? null : compact ? (
        <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
          <select data-testid="optin-relationship" aria-label="Your relationship to the person in addiction" className="funnel-input" style={{ ...inputStyle, appearance: 'none', WebkitAppearance: 'none', paddingRight: '2.25rem' }} value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })}>
            <option value="">Who are you? (optional)</option>
            {RELATIONSHIPS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <ChevronDown size={18} style={{ position: 'absolute', right: '0.9rem', top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
        </div>
      ) : (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 1rem' }}>
          <legend style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem', color: text, textAlign: 'left', padding: 0 }}>Who are you? <span style={{ fontWeight: 400, color: sub }}>(optional)</span></legend>
          <div style={{ display: 'grid', gap: '0.35rem', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
            {RELATIONSHIPS.map(([k, v]) => (
              <div key={k} data-testid={`optin-rel-${k}`} onClick={() => setForm({ ...form, relationship: k })} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.6rem', fontSize: '0.95rem', color: sub, cursor: 'pointer', textAlign: 'left' }}>
                <input type="radio" id={`optin-rel-${variant}-${k}`} name={`relationship-${variant}`} value={k} checked={form.relationship === k} onChange={() => setForm({ ...form, relationship: k })} aria-label={v} style={{ accentColor: C.sage, margin: 0, flexShrink: 0 }} /><span>{v}</span>
              </div>
            ))}
          </div>
        </fieldset>
      )}
      <AnimatePresence>{error && <motion.p data-testid="optin-error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ color: C.rust, fontSize: '0.9rem', margin: '0 0 0.75rem' }}>{error}</motion.p>}</AnimatePresence>
      <div style={{ textAlign: compact ? 'center' : 'left' }}>
      <button data-testid="optin-submit" type="submit" disabled={status === 'loading'} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: C.rust, color: '#fff', border: 0, padding: '0.9rem 1.6rem', borderRadius: '999px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', width: 'auto', opacity: status === 'loading' ? 0.7 : 1 }}>
        {status === 'loading' ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {copy.button}
      </button>
      </div>
      <p style={{ fontSize: '0.78rem', color: dark ? 'rgba(255,255,255,0.65)' : C.muted, lineHeight: 1.6, margin: '0.9rem 0 0', textAlign: compact ? 'center' : 'left' }}>
        We'll never share your email or send you anything you didn't ask for. Unsubscribe any time. Emails come from "Christian Family Recovery" with discreet subject lines.
      </p>
    </form>
  );
};
